import { SurveyIdProvider } from '../../../../hooks/SurveyIdContext'
import { AudienceSummaryContainer } from '../../../../modules/Audience/AudienceSummary'
import { QuestionnaireContextProvider } from '../../../../modules/Questionnaire/Questionnaire.context'

/**
 * Read-only audience summary for a single market, scoped by surveyId rather
 * than the Confirm page's master-survey provider. SurveyIdProvider only wins
 * where there is no `:surveyId` route param, which holds for the
 * project-scoped multi-market routes this dialog is opened from.
 *
 * `network-only` is the whole mechanism for the cross-market text collision
 * here: Apollo normalises audience Question entities by questionId, so the
 * fetch rewrites them with this market's text. Unlike the multi-market
 * audiences page we deliberately do *not* evict the shared ROOT_QUERY audience
 * fields — eviction is unscoped across args, and the host Confirm page holds a
 * cache-first watcher of `draftAudienceV2` for the master survey
 * (useMarketsTranslationWarnings) that gates each row's Launch button.
 *
 * Not evicting protects that watcher's ROOT_QUERY entry, but the fetch still
 * writes this market's data into the entities that entry holds refs to, so the
 * watcher re-renders reading them. That is contained rather than harmless: both
 * consumers use the shared fields only as the English `master`
 * (extractAudienceFields, completion's emptiness check), which stays non-empty
 * whichever market wrote it. The one input that could genuinely move is the
 * length of `criterionOptions` if a market's screener qualifies a different
 * option set than the master's for the same questionLk — that feeds the Launch
 * gate's completeness count. Not shown reachable; revisit here first if a
 * market's Launch button is ever wrongly gated while this dialog is open.
 */
export const MarketLaunchAudienceSummary = ({
  surveyId
}: {
  surveyId: string
}) => {
  return (
    <SurveyIdProvider value={surveyId}>
      {/* The summary runs questionnaire validation, which fires a fresh
          draftQuestionnaireV2 fetch for this market on every open. Without a
          nested provider that fetch would run against the host provider's
          handle and repoint the Confirm page's `draftQuestionnaire` at this
          market — useMarketsTranslationWarnings reads exactly that value, so
          this guards the same Launch gate `network-only` does, not just tidy
          state. The fetch still writes into questionLk/questionId entities the
          host's entries share; contained by the same consumer trace as above. */}
      <QuestionnaireContextProvider>
        <AudienceSummaryContainer
          showTitle
          audienceFetchPolicy="network-only"
          // The control's default empty state tells the user to edit targeting
          // and screening. This dialog has no edit affordance and is about to
          // lock the market, so state the consequence instead.
          emptySubtitle="This market has no targeting criteria or screening questions."
          emptyDescription="Its sample will be sent to the general population."
        />
      </QuestionnaireContextProvider>
    </SurveyIdProvider>
  )
}
